import Grid from '@mui/material/Grid';
import { Button, Drawer, TextField, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { ValidateDatePicker } from '../Generic/Forms/ValidateDatePicker';
import { useV1ReferralsModel } from '../Model/V1ReferralsModel';
import { usePolicy } from '../Model/PolicyModel';
import { CustomField, V1Referral } from '../GeneratedClient';
import { CustomFieldInput } from '../Generic/CustomFieldInput';

import {
  addReferralSchema,
  AddReferralFormValues,
} from '../V1Referrals/ReferralSchema';

interface EditReferralDrawerProps {
  referral: V1Referral;
  onClose: () => void;
}

export function EditReferralDrawer({
  referral,
  onClose,
}: EditReferralDrawerProps) {
  const policy = usePolicy();

  const { updateReferralDetails, updateCustomReferralField } =
    useV1ReferralsModel();

  const referralCustomFields: CustomField[] =
    policy.referralPolicy?.customFields ?? [];

  const {
    control,
    handleSubmit,
    formState: { dirtyFields, isSubmitting },
  } = useForm<AddReferralFormValues>({
    resolver: zodResolver(addReferralSchema),
    defaultValues: {
      openedAtLocal: referral.createdAtUtc,
      title: referral.title,
      familyId: referral.familyId ?? null,
      comment: referral.comment ?? '',
      customFields: Object.fromEntries(
        referralCustomFields.map((field) => [
          field.name,
          referral.completedCustomFields?.[field.name]?.value ?? undefined,
        ])
      ),
    },
  });

  const onSubmit = async (data: AddReferralFormValues) => {
    if (dirtyFields.openedAtLocal || dirtyFields.title || dirtyFields.comment) {
      await updateReferralDetails(referral.referralId, {
        openedAtUtc: data.openedAtLocal,
        title: data.title,
        comment: data.comment || undefined,
      });
    }

    for (const field of referralCustomFields) {
      if (!dirtyFields.customFields?.[field.name]) continue;

      const value = data.customFields?.[field.name];

      if (value !== undefined) {
        await updateCustomReferralField(referral.referralId, field, value);
      }
    }
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: 500,
            p: 3,
            top: 45,
          },
        },
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <Typography variant="h6">Edit Referral</Typography>
          </Grid>

          <Grid size={12}>
            <Controller
              name="openedAtLocal"
              control={control}
              render={({ field, fieldState }) => (
                <ValidateDatePicker
                  label="When was this referral opened?"
                  value={field.value}
                  disableFuture
                  onChange={(date) => field.onChange(date)}
                  textFieldProps={{
                    fullWidth: true,
                    required: true,
                    error: !!fieldState.error,
                    helperText: fieldState.error?.message,
                  }}
                />
              )}
            />
          </Grid>

          <Grid size={12}>
            <Controller
              name="title"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Referral Title"
                  fullWidth
                  required
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Grid>

          {referralCustomFields.length > 0 && (
            <Grid size={12}>
              <Grid container spacing={2}>
                {referralCustomFields.map((field) => (
                  <Grid size={12} key={field.name}>
                    <Typography
                      variant="body2"
                      sx={{ mb: 0.5 }}
                      className="ph-unmask"
                    >
                      {field.name}
                    </Typography>

                    <Controller
                      name={`customFields.${field.name}` as const}
                      control={control}
                      render={({ field: rhfField }) => (
                        <CustomFieldInput
                          customFieldPolicy={field}
                          value={rhfField.value}
                          onChange={rhfField.onChange}
                        />
                      )}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          )}

          <Grid size={12}>
            <Controller
              name="comment"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Referral Comment"
                  fullWidth
                  multiline
                  minRows={3}
                />
              )}
            />
          </Grid>

          <Grid size={12} sx={{ textAlign: 'right' }}>
            <Button
              color="secondary"
              variant="contained"
              sx={{ mr: 2 }}
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>

            <Button
              color="primary"
              variant="contained"
              type="submit"
              disabled={isSubmitting}
            >
              Save
            </Button>
          </Grid>
        </Grid>
      </form>
    </Drawer>
  );
}
